// src/ForgotPassword.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "./firebase";
import { sendPasswordResetEmail } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import logo from "./assets/wgwitlogov1white-02.png";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [usernameOrEmail, setUsernameOrEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState({ type: "", msg: "" });

  const looksLikeEmail = (s) => /\S+@\S+\.\S+/.test(s);

  const resolveEmail = async (inputRaw) => {
    const input = inputRaw.trim();
    if (!input) throw new Error("Please enter your email or username.");

    // If user typed an email, use it directly.
    if (looksLikeEmail(input)) return input;

    // Otherwise treat as username (lowercased)
    const uname = input.toLowerCase();
    const unameRef = doc(db, "usernames", uname);
    const unameSnap = await getDoc(unameRef);

    if (!unameSnap.exists()) {
      throw new Error("We couldn't find that username. Try your email instead.");
    }

    const data = unameSnap.data();
    if (data?.email) return data.email;

    if (data?.uid) {
      const pubRef = doc(db, "user_public", data.uid);
      const pubSnap = await getDoc(pubRef);
      if (pubSnap.exists() && pubSnap.data()?.email) {
        return pubSnap.data().email;
      }
    }

    throw new Error("No email is linked to that username. Try entering your email.");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setNotice({ type: "", msg: "" });
    setLoading(true);
    try {
      const email = await resolveEmail(usernameOrEmail);
      await sendPasswordResetEmail(auth, email);
      setNotice({
        type: "success",
        msg:
          "If an account exists for that email, a reset link has been sent. Check your inbox and spam.",
      });
    } catch (err) {
      setNotice({ type: "error", msg: err.message || "Reset failed." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-zinc-900 rounded-2xl p-6 shadow-lg">
        <div className="flex flex-col items-center gap-3 mb-6">
          <img src={logo} alt="WhatGradeWillItGet" className="h-10" />
          <h1 className="text-xl font-semibold">Forgot Password</h1>
          <p className="text-sm text-zinc-300 text-center">
            Enter your email or username. We’ll send a reset link to the email on file.
          </p>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          {/* ✅ Removed the redundant label above the field */}
          <input
            type="text"
            className="w-full rounded-xl bg-zinc-800 px-3 py-2 outline-none focus:ring-2 focus:ring-zinc-500"
            value={usernameOrEmail}
            onChange={(e) => setUsernameOrEmail(e.target.value)}
            placeholder="Email or Username"  // ✅ simplified placeholder text
            autoComplete="username"
            disabled={loading}
          />

          <button
            type="submit"
            disabled={loading || !usernameOrEmail.trim()}
            className="rounded-2xl px-4 py-2 bg-white text-black font-medium hover:opacity-90 disabled:opacity-50"
          >
            {loading ? "Sending..." : "Send reset link"}
          </button>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-2xl px-4 py-2 bg-zinc-800 text-white hover:bg-zinc-700"
          >
            Back
          </button>
        </form>

        {notice.msg ? (
          <div
            className={`mt-4 text-sm rounded-xl p-3 ${
              notice.type === "success" ? "bg-green-900/40" : "bg-red-900/40"
            }`}
          >
            {notice.msg}
          </div>
        ) : null}
      </div>
    </div>
  );
}
